// src/pages/XeroSyncPage.js
// Xero sync page — Contact sync (Phase 2).

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import xeroService from '../services/xeroService';

// ── Helpers ───────────────────────────────────────────────────────────────────

const initSelections = (newInXero) =>
  Object.fromEntries(newInXero.map(({ xero }) => [xero.xeroContactId, false]));

// ── Contact Sync Tab ──────────────────────────────────────────────────────────

function ContactSyncTab() {
  const [loading, setLoading]   = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const [conflictChoices, setConflictChoices]   = useState({});
  const [importSelections, setImportSelections] = useState({});

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const data = await xeroService.previewContacts();
      setPreview(data);
      setImportSelections(initSelections(data.newInXero || []));
      const choices = {};
      (data.conflicts || []).forEach(({ xero }) => { choices[xero.xeroContactId] = 'keep'; });
      setConflictChoices(choices);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleApply = async () => {
    setApplying(true);
    setError('');
    setSuccess('');
    try {
      const link = (preview.matched || [])
        .filter(m => !m.quicklog.xeroContactId)
        .map(m => ({ quicklogId: m.quicklog.id, xeroContactId: m.xero.xeroContactId }));

      const overwrite = (preview.conflicts || [])
        .filter(c => conflictChoices[c.xero.xeroContactId] === 'use_xero')
        .map(c => ({
          quicklogId:    c.quicklog.id,
          xeroContactId: c.xero.xeroContactId,
          fields:        c.diffFields || ['email'],
        }));

      const linkConflicts = (preview.conflicts || [])
        .filter(c => conflictChoices[c.xero.xeroContactId] === 'keep')
        .map(c => ({ quicklogId: c.quicklog.id, xeroContactId: c.xero.xeroContactId }));

      const importList = Object.entries(importSelections)
        .filter(([, selected]) => selected)
        .map(([xeroContactId]) => ({ xeroContactId }));

      const result = await xeroService.applyContacts({
        link:      [...link, ...linkConflicts],
        import:    importList,
        overwrite,
      });

      const msg = [
        result.linked      && `${result.linked} linked`,
        result.imported    && `${result.imported} imported`,
        result.overwritten && `${result.overwritten} updated`,
      ].filter(Boolean).join(', ');

      setSuccess(`Sync applied${msg ? ': ' + msg : ''}. Refreshing…`);
      setTimeout(() => loadPreview(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to apply sync');
    } finally {
      setApplying(false);
    }
  };

  const selectedImportCount  = Object.values(importSelections).filter(Boolean).length;
  const pendingMatchCount    = (preview?.matched || []).filter(m => !m.quicklog.xeroContactId).length;
  const hasAnything = pendingMatchCount > 0 ||
    (preview?.conflicts || []).length > 0 ||
    selectedImportCount > 0;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">Contact Sync</h5>
          <small className="text-muted">Match quickLOG customers with Xero contacts</small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={loadPreview} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-danger py-2 px-3" style={{ fontSize: 13 }}>{error}</div>}
      {success && <div className="alert alert-success py-2 px-3" style={{ fontSize: 13 }}>{success}</div>}

      {loading && (
        <div className="text-center py-5 text-muted">
          <span className="spinner-border spinner-border-sm me-2" />Loading Xero contacts…
        </div>
      )}

      {preview && !loading && (
        <>
          {/* ── Auto-matched ── */}
          <Section
            title="Auto-matched"
            badge={preview.matched?.length || 0}
            badgeColor="success"
            hint="These will be linked automatically when you apply."
            defaultOpen
          >
            {(preview.matched || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No matches found.</p>
            ) : (
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>quickLOG</th>
                    <th>Xero</th>
                    <th>Match via</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.matched.map(m => (
                    <tr key={m.xero.xeroContactId}>
                      <td>{m.quicklog.name}</td>
                      <td>{m.xero.name}</td>
                      <td className="text-muted">{m.matchReason || 'ID'}</td>
                      <td>
                        {m.quicklog.xeroContactId
                          ? <span className="badge bg-success">Already linked</span>
                          : <span className="badge bg-primary">Will link</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* ── Conflicts ── */}
          <Section
            title="Conflicts"
            badge={preview.conflicts?.length || 0}
            badgeColor="warning"
            hint="Same name found but with different data. Choose which version to keep."
            defaultOpen={(preview.conflicts || []).length > 0}
          >
            {(preview.conflicts || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No conflicts.</p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {preview.conflicts.map(c => (
                  <div key={c.xero.xeroContactId} className="border rounded p-3" style={{ fontSize: 13 }}>
                    <div className="row g-3 mb-2">
                      <div className="col-md-5">
                        <div className="fw-semibold mb-1 text-muted" style={{ fontSize: 11 }}>QUICKLOG</div>
                        <div><strong>{c.quicklog.name}</strong></div>
                        <div className="text-muted">{c.quicklog.email || '—'}</div>
                        <div className="text-muted">{c.quicklog.address || '—'}</div>
                      </div>
                      <div className="col-md-2 d-flex align-items-center justify-content-center">
                        <span className="text-muted">⟷</span>
                      </div>
                      <div className="col-md-5">
                        <div className="fw-semibold mb-1 text-muted" style={{ fontSize: 11 }}>XERO</div>
                        <div><strong>{c.xero.name}</strong></div>
                        <div className="text-muted">{c.xero.email || '—'}</div>
                        <div className="text-muted">{c.xero.address || '—'}</div>
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          id={`keep-${c.xero.xeroContactId}`}
                          checked={conflictChoices[c.xero.xeroContactId] === 'keep'}
                          onChange={() => setConflictChoices(p => ({ ...p, [c.xero.xeroContactId]: 'keep' }))}
                        />
                        <label className="form-check-label" htmlFor={`keep-${c.xero.xeroContactId}`}>
                          Keep quickLOG data (just link IDs)
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          id={`xero-${c.xero.xeroContactId}`}
                          checked={conflictChoices[c.xero.xeroContactId] === 'use_xero'}
                          onChange={() => setConflictChoices(p => ({ ...p, [c.xero.xeroContactId]: 'use_xero' }))}
                        />
                        <label className="form-check-label" htmlFor={`xero-${c.xero.xeroContactId}`}>
                          Use Xero data (overwrite {(c.diffFields || []).join(', ')})
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* ── New in Xero ── */}
          <Section
            title="New in Xero"
            badge={preview.newInXero?.length || 0}
            badgeColor="info"
            hint="These contacts exist in Xero but not in quickLOG. Select which ones to import."
            defaultOpen={(preview.newInXero || []).length > 0}
          >
            {(preview.newInXero || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No new Xero contacts to import.</p>
            ) : (
              <>
                <div className="d-flex gap-2 mb-2">
                  <button
                    className="btn btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setImportSelections(Object.fromEntries(preview.newInXero.map(({ xero }) => [xero.xeroContactId, true])))}
                  >
                    Select all
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setImportSelections(initSelections(preview.newInXero))}
                  >
                    Deselect all
                  </button>
                </div>
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr><th style={{ width: 36 }}></th><th>Name</th><th>Email</th><th>Phone</th></tr>
                  </thead>
                  <tbody>
                    {preview.newInXero.map(({ xero }) => (
                      <tr key={xero.xeroContactId}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!importSelections[xero.xeroContactId]}
                            onChange={e => setImportSelections(p => ({ ...p, [xero.xeroContactId]: e.target.checked }))}
                          />
                        </td>
                        <td>{xero.name}</td>
                        <td className="text-muted">{xero.email || '—'}</td>
                        <td className="text-muted">{xero.phone || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Section>

          {/* ── Unlinked (info only) ── */}
          {(preview.unlinked || []).length > 0 && (
            <Section
              title="Not in Xero"
              badge={preview.unlinked.length}
              badgeColor="secondary"
              hint="These quickLOG customers have no Xero link. No action needed — they'll be linked when matched."
              defaultOpen={false}
            >
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>Name</th><th>Email</th></tr></thead>
                <tbody>
                  {preview.unlinked.map(({ quicklog: ql }) => (
                    <tr key={ql.id}>
                      <td>{ql.name}</td>
                      <td className="text-muted">{ql.email || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* ── Apply button ── */}
          <div className="mt-4 d-flex align-items-center gap-3">
            <button
              className="btn btn-primary"
              onClick={handleApply}
              disabled={applying || !hasAnything}
            >
              {applying
                ? <><span className="spinner-border spinner-border-sm me-2" />Applying…</>
                : `Apply Sync${selectedImportCount > 0 ? ` (import ${selectedImportCount})` : ''}`}
            </button>
            {!hasAnything && (
              <span className="text-muted" style={{ fontSize: 13 }}>Nothing to apply — all contacts are already in sync.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Item Sync Tab ─────────────────────────────────────────────────────────────

function ItemSyncTab() {
  const [loading, setLoading]   = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const [manualMatches, setManualMatches]           = useState({});
  const [acceptedSuggestions, setAcceptedSuggestions] = useState({});
  const [relinkMatches, setRelinkMatches]           = useState({});

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const data = await xeroService.previewItems();
      setPreview(data);
      const accepted = {};
      (data.suggestions || []).forEach(s => {
        if (s.confidence === 'exact') accepted[s.priceListItem.id] = true;
      });
      setAcceptedSuggestions(accepted);
      setManualMatches({});
      setRelinkMatches({});
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleApply = async () => {
    setApplying(true);
    setError('');
    setSuccess('');
    try {
      const link = [];

      // Accepted suggestions
      (preview.suggestions || []).forEach(s => {
        if (acceptedSuggestions[s.priceListItem.id]) {
          link.push({ priceListItemId: s.priceListItem.id, xeroItemCode: s.suggestedItem.code });
        }
      });

      // Manual matches
      (preview.unmatched || []).forEach(u => {
        const chosen = manualMatches[u.priceListItem.id];
        if (chosen) link.push({ priceListItemId: u.priceListItem.id, xeroItemCode: chosen });
      });

      // Re-links (changes or unlinks on already-linked items)
      Object.entries(relinkMatches).forEach(([id, newCode]) => {
        link.push({ priceListItemId: Number(id), xeroItemCode: newCode || null });
      });

      if (link.length === 0) {
        setError('Nothing to apply — accept a suggestion, choose a manual match, or change a linked item.');
        setApplying(false);
        return;
      }

      const result = await xeroService.applyItems({ link });
      setSuccess(`Done: ${result.linked} item(s) updated.${result.errors?.length ? ` ${result.errors.length} error(s).` : ''}`);
      setTimeout(() => loadPreview(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to apply sync');
    } finally {
      setApplying(false);
    }
  };

  const pendingCount =
    (preview?.suggestions || []).filter(s => acceptedSuggestions[s.priceListItem.id]).length +
    Object.values(manualMatches).filter(Boolean).length +
    Object.keys(relinkMatches).length;

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-0">Product / Item Sync</h5>
          <small className="text-muted">Link quickLOG price list items to Xero items</small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={loadPreview} disabled={loading}>
          {loading ? <span className="spinner-border spinner-border-sm" /> : 'Refresh'}
        </button>
      </div>

      {error && <div className="alert alert-danger py-2 px-3" style={{ fontSize: 13 }}>{error}</div>}
      {success && <div className="alert alert-success py-2 px-3" style={{ fontSize: 13 }}>{success}</div>}

      {loading && (
        <div className="text-center py-5 text-muted">
          <span className="spinner-border spinner-border-sm me-2" />Loading Xero items…
        </div>
      )}

      {preview && !loading && (
        <>
          {/* ── Already linked ── */}
          <Section
            title="Already linked"
            badge={preview.linked?.length || 0}
            badgeColor="success"
            hint="These price list items are linked. Use the dropdown to change or remove the link."
            defaultOpen={(preview.linked || []).length > 0}
          >
            {(preview.linked || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>None linked yet.</p>
            ) : (
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>quickLOG item</th><th>Xero item</th></tr></thead>
                <tbody>
                  {preview.linked.map(l => {
                    const currentVal = l.priceListItem.id in relinkMatches
                      ? relinkMatches[l.priceListItem.id]
                      : l.xeroItemCode;
                    const changed = l.priceListItem.id in relinkMatches;
                    return (
                      <tr key={l.priceListItem.id}>
                        <td>{l.priceListItem.Product?.name || l.priceListItem.presentationLabel}</td>
                        <td>
                          <select
                            className={`form-select form-select-sm${changed ? ' border-warning' : ''}`}
                            style={{ fontSize: 12, maxWidth: 300 }}
                            value={currentVal || ''}
                            onChange={e => {
                              const val = e.target.value;
                              if (val === l.xeroItemCode) {
                                setRelinkMatches(p => { const n = { ...p }; delete n[l.priceListItem.id]; return n; });
                              } else {
                                setRelinkMatches(p => ({ ...p, [l.priceListItem.id]: val }));
                              }
                            }}
                          >
                            <option value="">— unlink —</option>
                            {(preview.xeroItems || []).map(xi => (
                              <option key={xi.code} value={xi.code}>{xi.name} ({xi.code})</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Section>

          {/* ── Suggestions ── */}
          <Section
            title="Suggested matches"
            badge={preview.suggestions?.length || 0}
            badgeColor="primary"
            hint="Name or SKU-based matches — confirm or reject each one."
            defaultOpen={(preview.suggestions || []).length > 0}
          >
            {(preview.suggestions || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No suggestions.</p>
            ) : (
              <>
                <div className="d-flex gap-2 mb-2">
                  <button
                    className="btn btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setAcceptedSuggestions(Object.fromEntries((preview.suggestions || []).map(s => [s.priceListItem.id, true])))}
                  >
                    Accept all
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    style={{ fontSize: 12, padding: '2px 8px' }}
                    onClick={() => setAcceptedSuggestions({})}
                  >
                    Reject all
                  </button>
                </div>
                <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th>quickLOG item</th>
                      <th>Xero item</th>
                      <th>Code</th>
                      <th>Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.suggestions.map(s => (
                      <tr key={s.priceListItem.id}>
                        <td>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!acceptedSuggestions[s.priceListItem.id]}
                            onChange={e => setAcceptedSuggestions(p => ({ ...p, [s.priceListItem.id]: e.target.checked }))}
                          />
                        </td>
                        <td>{s.priceListItem.Product?.name || s.priceListItem.presentationLabel}</td>
                        <td>{s.suggestedItem.name}</td>
                        <td><code style={{ fontSize: 12 }}>{s.suggestedItem.code}</code></td>
                        <td>
                          <span className={`badge ${s.confidence === 'exact' ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: 10 }}>
                            {s.confidence}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Section>

          {/* ── Manual match ── */}
          <Section
            title="Manual match"
            badge={preview.unmatched?.length || 0}
            badgeColor="warning"
            hint="No automatic match found — select the Xero item for each."
            defaultOpen={(preview.unmatched || []).length > 0}
          >
            {(preview.unmatched || []).length === 0 ? (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>All items have a match.</p>
            ) : (
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>quickLOG item</th><th>Xero item</th></tr></thead>
                <tbody>
                  {preview.unmatched.map(u => (
                    <tr key={u.priceListItem.id}>
                      <td>{u.priceListItem.Product?.name || u.priceListItem.presentationLabel}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          style={{ fontSize: 12, maxWidth: 300 }}
                          value={manualMatches[u.priceListItem.id] || ''}
                          onChange={e => setManualMatches(p => ({ ...p, [u.priceListItem.id]: e.target.value }))}
                        >
                          <option value="">— skip —</option>
                          {(preview.xeroItems || []).map(xi => (
                            <option key={xi.code} value={xi.code}>{xi.name} ({xi.code})</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>

          {/* ── Not in any price list (info) ── */}
          {(preview.notInPriceList || []).length > 0 && (
            <Section
              title="Not in any price list"
              badge={preview.notInPriceList.length}
              badgeColor="secondary"
              hint="These products exist in your catalog but aren't on any price list — add them to a price list to enable Xero linking."
              defaultOpen={false}
            >
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>Product</th><th>SKU</th></tr></thead>
                <tbody>
                  {preview.notInPriceList.map(p => (
                    <tr key={p.id}><td>{p.name}</td><td className="text-muted">{p.sku || '—'}</td></tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* ── In Xero only (info) ── */}
          {(preview.newInXero || []).length > 0 && (
            <Section
              title="In Xero only"
              badge={preview.newInXero.length}
              badgeColor="secondary"
              hint="Xero items not referenced by any price list item. No action needed."
              defaultOpen={false}
            >
              <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                <thead><tr><th>Name</th><th>Code</th></tr></thead>
                <tbody>
                  {preview.newInXero.map(xi => (
                    <tr key={xi.code}>
                      <td>{xi.name}</td>
                      <td><code style={{ fontSize: 12 }}>{xi.code}</code></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}

          {/* ── Apply ── */}
          <div className="mt-4 d-flex align-items-center gap-3">
            <button
              className="btn btn-primary"
              onClick={handleApply}
              disabled={applying || pendingCount === 0}
            >
              {applying
                ? <><span className="spinner-border spinner-border-sm me-2" />Applying…</>
                : `Apply Sync${pendingCount > 0 ? ` (${pendingCount} item${pendingCount > 1 ? 's' : ''})` : ''}`}
            </button>
            {pendingCount === 0 && (
              <span className="text-muted" style={{ fontSize: 13 }}>Accept suggestions or choose manual matches to apply.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Collapsible section wrapper ───────────────────────────────────────────────

function Section({ title, badge, badgeColor = 'secondary', hint, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="card mb-3">
      <div
        className="card-header d-flex align-items-center gap-2"
        style={{ cursor: 'pointer', userSelect: 'none', fontSize: 14 }}
        onClick={() => setOpen(o => !o)}
      >
        <span className="fw-semibold">{title}</span>
        <span className={`badge bg-${badgeColor}`}>{badge}</span>
        {hint && <span className="text-muted ms-1" style={{ fontSize: 12, fontWeight: 400 }}>{hint}</span>}
        <span className="ms-auto text-muted" style={{ fontSize: 12 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && <div className="card-body">{children}</div>}
    </div>
  );
}

// ── Page shell ────────────────────────────────────────────────────────────────

export default function XeroSyncPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'contacts';

  const setTab = (t) => setSearchParams({ tab: t });

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 900 }}>
      <div className="d-flex align-items-center gap-3 mb-4">
        <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/admin/settings')}>
          ← Settings
        </button>
        <h4 className="mb-0">Xero Sync</h4>
      </div>

      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'contacts' ? ' active' : ''}`}
            onClick={() => setTab('contacts')}
          >
            Contacts
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${tab === 'items' ? ' active' : ''}`}
            onClick={() => setTab('items')}
          >
            Products / Items
          </button>
        </li>
      </ul>

      {tab === 'contacts' && <ContactSyncTab />}
      {tab === 'items'    && <ItemSyncTab />}
    </div>
  );
}
